/**
 * UserTable Component
 * 
 * Displays users in a table format with sorting, filtering, and actions.
 * Used in the User Management screen for managers.
 * 
 * @module UserTable
 */

import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Key,
  Mail,
  Shield,
  UserCheck,
  MoreVertical,
  Phone,
  MapPin
} from 'lucide-react';
import { UserProfile, UserRole } from '../../@types/user';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';

/**
 * UserTable props interface
 */
export interface UserTableProps {
  /** Users to display */
  users: UserProfile[];
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Handle row click */
  onRowClick?: (user: UserProfile) => void;
  
  /** Handle edit action */
  onEdit?: (user: UserProfile) => void;
  
  /** Handle delete action */
  onDelete?: (user: UserProfile) => void;
  
  /** Handle reset password action */
  onResetPassword?: (user: UserProfile) => void;
  
  /** Handle view action */
  onView?: (user: UserProfile) => void;
  
  /** Enable row selection */
  selectable?: boolean;
  
  /** Selected user IDs */
  selectedUserIds?: string[];
  
  /** Handle selection change */
  onSelectionChange?: (userIds: string[]) => void;
  
  /** Show actions column */
  showActions?: boolean;
}

/**
 * Styled components
 */
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background: white;
  border-radius: 12px;
  box-shadow: ${Shadows.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1000px;
`;

const TableHead = styled.thead`
  background-color: ${ThemeColors.background.tertiary};
`;

const TableRow = styled.tr<{ clickable?: boolean; selected?: boolean }>`
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  background-color: ${props => props.selected ? ThemeColors.primary.light : 'white'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.selected 
      ? ThemeColors.primary.light 
      : ThemeColors.background.secondary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${ThemeColors.border.light};
  }
`;

const TableHeader = styled.th`
  padding: ${Spacing.md} ${Spacing.lg};
  text-align: left;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;

  &:first-child {
    border-radius: 12px 0 0 0;
  }

  &:last-child {
    border-radius: 0 12px 0 0;
  }
`;

const TableCell = styled.td`
  padding: ${Spacing.md} ${Spacing.lg};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  vertical-align: middle;
`;


const RoleBadge = styled.span<{ role: UserRole }>`
  display: inline-flex;
  align-items: center;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 16px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  gap: ${Spacing.xs};
  background-color: ${props => {
    switch (props.role) {
      case 'manager': return ThemeColors.primary.light;
      case 'sales-representative': return ThemeColors.info.light;
      case 'admin': return ThemeColors.warning.light;
      default: return ThemeColors.background.tertiary;
    }
  }};
  color: ${props => {
    switch (props.role) {
      case 'manager': return ThemeColors.primary.dark;
      case 'sales-representative': return ThemeColors.info.dark;
      case 'admin': return ThemeColors.warning.dark;
      default: return ThemeColors.text.secondary;
    }
  }};

  svg {
    width: 12px;
    height: 12px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: ${Spacing.xs};
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  transition: all 0.2s ease;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.primary.main};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ActionsCell = styled(TableCell)`
  display: flex;
  gap: ${Spacing.xs};
  align-items: center;
  justify-content: flex-end;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${ThemeColors.primary.main};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${Typography.fontWeight.semibold};
  font-size: ${Typography.fontSize.sm};
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
`;

const UserEmail = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin-top: ${Spacing.xs};
`;

const LocationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin-top: ${Spacing.xs};
`;

const EmptyState = styled.div`
  padding: ${Spacing.xxl} ${Spacing.lg};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

const LoadingRow = styled(TableRow)`
  background: linear-gradient(
    90deg,
    ${ThemeColors.background.secondary} 25%,
    ${ThemeColors.background.tertiary} 50%,
    ${ThemeColors.background.secondary} 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const LastLogin = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
`;


/**
 * Get role icon based on user role
 */
const getRoleIcon = (role: UserRole) => {
  switch (role) {
    case 'manager':
      return <Shield />;
    case 'sales-representative':
      return <UserCheck />;
    case 'admin':
      return <Shield />;
    default:
      return <UserCheck />;
  }
};

/**
 * Format date for display
 */
const formatDate = (date: Date | string): string => {
  if (!date) return 'Never';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format last login for display
 */
const formatLastLogin = (date: Date | string | undefined): string => {
  if (!date) return 'Never';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInHours = (now.getTime() - d.getTime()) / (1000 * 60 * 60);
  
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
  if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Get user initials for avatar
 */
const getUserInitials = (user: UserProfile): string => {
  return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
};

/**
 * UserTable Component
 * 
 * @example
 * ```tsx
 * <UserTable
 *   users={users}
 *   isLoading={isLoading}
 *   onRowClick={(user) => navigate(`/user/${user.id}`)}
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 *   showActions
 * />
 * ```
 */
export const UserTable: React.FC<UserTableProps> = ({
  users,
  isLoading = false,
  onRowClick,
  onEdit,
  onDelete,
  onResetPassword,
  onView,
  selectable = false,
  selectedUserIds = [],
  onSelectionChange,
  showActions = true
}) => {
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedUserIds);

  /**
   * Handle row selection
   */
  const handleRowSelect = (userId: string, checked: boolean) => {
    const newSelection = checked
      ? [...localSelectedIds, userId]
      : localSelectedIds.filter(id => id !== userId);
    
    setLocalSelectedIds(newSelection);
    onSelectionChange?.(newSelection);
  };

  /**
   * Handle select all
   */
  const handleSelectAll = (checked: boolean) => {
    const newSelection = checked ? users.map(u => u.id) : [];
    setLocalSelectedIds(newSelection);
    onSelectionChange?.(newSelection);
  };

  /**
   * Check if all rows are selected
   */
  const allSelected = useMemo(() => {
    return users.length > 0 && localSelectedIds.length === users.length;
  }, [users, localSelectedIds]);

  if (isLoading) {
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {selectable && <TableHeader>Select</TableHeader>}
              <TableHeader>User</TableHeader>
              <TableHeader>Role</TableHeader>
              <TableHeader>Territory</TableHeader>
              <TableHeader>Last Login</TableHeader>
              <TableHeader>Created</TableHeader>
              {showActions && <TableHeader>Actions</TableHeader>}
            </TableRow>
          </TableHead>
          <tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <LoadingRow key={i}>
                {selectable && <TableCell></TableCell>}
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                {showActions && <TableCell></TableCell>}
              </LoadingRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    );
  }

  if (users.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <p>No users found</p>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {selectable && (
              <TableHeader>
                <Checkbox
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableHeader>
            )}
            <TableHeader>User</TableHeader>
            <TableHeader>Role</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Territory</TableHeader>
            <TableHeader>Last Login</TableHeader>
            <TableHeader>Created</TableHeader>
            {showActions && <TableHeader>Actions</TableHeader>}
          </TableRow>
        </TableHead>
        <tbody>
          {users.map((user) => (
            <TableRow
              key={user.id}
              clickable={!!onRowClick}
              selected={localSelectedIds.includes(user.id)}
              onClick={() => !selectable && onRowClick?.(user)}
            >
              {selectable && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    type="checkbox"
                    checked={localSelectedIds.includes(user.id)}
                    onChange={(e) => handleRowSelect(user.id, e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell>
                <UserInfo>
                  <Avatar>
                    {getUserInitials(user)}
                  </Avatar>
                  <UserDetails>
                    <UserName>{user.displayName}</UserName>
                    <UserEmail>{user.email}</UserEmail>
                    {user.phoneNumber && (
                      <ContactInfo>
                        <Phone size={12} />
                        {user.phoneNumber}
                      </ContactInfo>
                    )}
                    {user.territory && (
                      <LocationInfo>
                        <MapPin size={12} />
                        {user.territory}
                      </LocationInfo>
                    )}
                  </UserDetails>
                </UserInfo>
              </TableCell>
              <TableCell>
                <RoleBadge role={user.role}>
                  {getRoleIcon(user.role)}
                  {user.role === 'sales-representative' ? 'Sales Rep' : user.role}
                </RoleBadge>
              </TableCell>
              <TableCell>
                {user.role === 'manager' 
                  ? 'National'
                  : (() => {
                      // Get sales groups from user (prefer array, fallback to single string)
                      const salesGroups = (user as any).salesGroups && Array.isArray((user as any).salesGroups)
                        ? (user as any).salesGroups
                        : user.salesGroup
                        ? [user.salesGroup]
                        : [];
                      
                      return salesGroups.length > 0 
                        ? salesGroups.join(', ')
                        : 'N/A';
                    })()}
              </TableCell>
              <TableCell>
                <LastLogin>{formatLastLogin(user.lastLoginAt)}</LastLogin>
              </TableCell>
              <TableCell>
                {formatDate(user.createdAt)}
              </TableCell>
              {showActions && (
                <ActionsCell onClick={(e) => e.stopPropagation()}>
                  {onView && (
                    <ActionButton
                      onClick={() => onView(user)}
                      title="View user details"
                    >
                      <Eye />
                    </ActionButton>
                  )}
                  {onEdit && (
                    <ActionButton
                      onClick={() => onEdit(user)}
                      title="Edit user"
                    >
                      <Edit />
                    </ActionButton>
                  )}
                  {onResetPassword && (
                    <ActionButton
                      onClick={() => onResetPassword(user)}
                      title="Reset password"
                      style={{ color: ThemeColors.info.main }}
                    >
                      <Key />
                    </ActionButton>
                  )}
                  {onDelete && (
                    <ActionButton
                      onClick={() => onDelete(user)}
                      title="Delete user"
                      style={{ color: ThemeColors.error.main }}
                    >
                      <Trash2 />
                    </ActionButton>
                  )}
                </ActionsCell>
              )}
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};